import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import App from "../portal.jsx";
import { DialogHost } from "./components/common/DialogHost.jsx";
import { HelpModalHost } from "./components/common/HelpModalHost.jsx";
import { logger } from "./utils/logger";
import "./index.css";
import { C } from "./utils/constants";

// The CRM module is lazy-loaded so its editor library (Quill) never ships in the
// main portal bundle -- it only downloads when an admin opens the /crm tab.
const CrmApp = React.lazy(() => import("./crm/CrmApp.jsx"));
// The Feedback module is its own self-contained tab too (docs/feedback-module-plan.md),
// lazy-loaded so it only downloads when someone opens the /feedback tab.
const FeedbackApp = React.lazy(() => import("./feedback/FeedbackApp.jsx"));
// Telemarketing is a self-contained tab module (docs/telemarketing-module-plan.md),
// lazy-loaded so it only downloads when a Supervisor opens the /telemarketing tab.
const TelemarketingApp = React.lazy(() => import("./telemarketing/TelemarketingApp.jsx"));

// Root path switch: the self-contained module tabs render for /crm* and
// /feedback*, the main portal for everything else. Branching here (rather than
// nesting a <Route> around <App/>) keeps App's own absolute <Routes> intact and
// leaves portal.jsx untouched — see docs/crm-module-plan.md §3.2.
const LazyFallback = (
  <div style={{ padding: 24, fontFamily: "Inter, sans-serif", color: "#555" }}>Loading...</div>
);
function Root() {
  const { pathname } = useLocation();
  if (pathname === "/crm" || pathname.startsWith("/crm/")) {
    return <React.Suspense fallback={LazyFallback}>{<CrmApp />}</React.Suspense>;
  }
  if (pathname === "/feedback" || pathname.startsWith("/feedback/")) {
    return <React.Suspense fallback={LazyFallback}>{<FeedbackApp />}</React.Suspense>;
  }
  if (pathname === "/telemarketing" || pathname.startsWith("/telemarketing/")) {
    return <React.Suspense fallback={LazyFallback}>{<TelemarketingApp />}</React.Suspense>;
  }
  return <App />;
}

// Error Boundary — catches crashes and shows a friendly message instead of a white screen
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }
  componentDidCatch(error, info) {
    logger.error("App crash:", error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return React.createElement(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            fontFamily: "Inter, sans-serif",
            background: "#f5f5f5",
          },
        },
        React.createElement(
          "div",
          {
            style: {
              background: "white",
              borderRadius: 12,
              padding: 40,
              maxWidth: 440,
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            },
          },
          React.createElement("div", { style: { fontSize: 48, marginBottom: 16 } }, "\u26A0\uFE0F"),
          React.createElement(
            "h2",
            { style: { margin: "0 0 12px", color: "#333" } },
            "Something went wrong"
          ),
          React.createElement(
            "p",
            { style: { color: "#666", fontSize: 14, lineHeight: 1.6, margin: "0 0 20px" } },
            "The page encountered an error. Please refresh to try again. If the problem continues, contact support."
          ),
          React.createElement(
            "button",
            {
              onClick: function () {
                window.location.reload();
              },
              style: {
                background: C.teal || "#26A69A",
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              },
            },
            "Refresh Page"
          )
        )
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <ErrorBoundary>
    {/* BrowserRouter gives the app real URLs + browser Back/Forward + deep links.
        App mirrors its `page` state to the URL (see portal.jsx). */}
    <BrowserRouter>
      <Root />
      {/* App-wide in-app dialog host — renders null until a dialog is queued. */}
      <DialogHost />
      {/* Shared help modal — renders null until a Resources #help= link opens it. */}
      <HelpModalHost />
    </BrowserRouter>
  </ErrorBoundary>
);
